
import React, { useState, useMemo, useEffect } from 'react';
import { Button } from "@/ui-components/ui/button";
import { Badge } from "@/ui-components/ui/badge";
import { EditTemplateDialog } from "../components";
import { CreateTemplateDialog } from "../components/CreateTemplateDialog";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { PrimaryActionButton } from "@/util-components/inputs/primary-action-button";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";
import {
  LayoutTemplate,
  Info,
  Download,
  Plus,
  PenTool,
  Copy
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Template } from "../core/template.schemas";
import { 
  useTemplates2FindAll, 
  useTemplates2GetCategories,
  useTemplates2Duplicate,
  useTemplates2BulkDelete,
  useTemplates2FindOne
} from "@/lib/api/requests/templates.requests";
import { TemplatesFilterParams } from "@/lib/api/interfaces/templates.interface";
import { DeleteTemplateDialog } from "../components/DeleteTemplateDialog";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";
import { TemplateInfoDialog } from "../components/TemplateInfoDialog";
import { usePermissions } from "@/lib/hooks/use-permissions";
import { toast } from "@/lib/toast";
import {
  DataView,
  DataViewToolbar,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { PageInfoBanner, SubPageLayout } from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import {
  useOnboardingImportedTemplateId,
  useOnboardingTourStep,
} from "@/util-components/onboarding/tour-step-store";

import { UpgradePlanDialog } from "@/util-components/dialog/UpgradePlanDialog";

const DEFAULT_PAGE_SIZE = 18;

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryTemplateId = searchParams.get('templateId');

  const { state, setInfoBannerVisible, setViewMode, setSettings } = usePageState('post-generator-templates', {
    isInfoBannerVisible: true,
    viewMode: 'grid',
    settings: {
      selectedCategory: "all"
    }
  });
  const { can } = usePermissions();
  const duplicateMutation = useTemplates2Duplicate();
  const bulkDeleteMutation = useTemplates2BulkDelete();
  const onboardingTourStep = useOnboardingTourStep();
  const highlightedTemplateId = useOnboardingImportedTemplateId();
  const isOnboardingImportFlow = onboardingTourStep === 'templates-home';

  const activeHighlightedTemplateId = queryTemplateId || highlightedTemplateId;
  const isHighlightActive = !!queryTemplateId || isOnboardingImportFlow;

  const [searchTerm, setSearchTerm] = useState("");
  const selectedCategory = state.settings?.selectedCategory || "all";
  const [currentPage, setCurrentPage] = useState(1);
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [lastFilterKey, setLastFilterKey] = useState<string>('');
  
  const viewMode = state.viewMode || 'grid';
  
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [, setSelectedTemplate] = useState<Template | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState<Template | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const currentFilterKey = useMemo(() => {
    return `${searchTerm.trim()}-${selectedCategory}`;
  }, [searchTerm, selectedCategory]);

  const filterParams: TemplatesFilterParams = useMemo(() => ({
    textSearch: searchTerm.trim() || undefined,
    categories: selectedCategory === "all" ? undefined : selectedCategory === "Unkategorisiert" ? [] : [selectedCategory],
    page: currentPage,
    limit: DEFAULT_PAGE_SIZE,
    sortBy: 'updated',
    direction: 'desc'
  }), [searchTerm, selectedCategory, currentPage]);

  const templatesQuery = useTemplates2FindAll(filterParams, { enabled: true });
  const categoriesQuery = useTemplates2GetCategories({ enabled: true });

  // Fallback query if the requested template is not in the current list
  const isTemplateMissing = !!queryTemplateId && !templatesQuery.isLoading && allTemplates.length > 0 && !allTemplates.some(t => t.id === queryTemplateId);
  const singleTemplateQuery = useTemplates2FindOne(queryTemplateId || "", {
    enabled: isTemplateMissing,
    skipGlobalErrorHandler: true
  });

  useEffect(() => {
    if (currentFilterKey !== lastFilterKey) {
      setCurrentPage(1);
      setAllTemplates([]);
      setLastFilterKey(currentFilterKey);
    }
  }, [currentFilterKey, lastFilterKey]);

  useEffect(() => {
    if (templatesQuery.data?.docs) {
      if (currentPage === 1) {
        setAllTemplates(templatesQuery.data.docs);
      } else {
        setAllTemplates(prev => {
          const newDocs = templatesQuery.data?.docs || [];
          const existingIds = new Set(prev.map(a => a.id));
          const filteredNewDocs = newDocs.filter(d => !existingIds.has(d.id));
          return [...prev, ...filteredNewDocs];
        });
      }
    }
  }, [templatesQuery.data, currentPage]);

  useEffect(() => {
    if (singleTemplateQuery.data && !allTemplates.some(t => t.id === singleTemplateQuery.data.id)) {
      setAllTemplates(prev => [singleTemplateQuery.data, ...prev]);
    }
  }, [singleTemplateQuery.data, allTemplates]);

  useEffect(() => {
    if (queryTemplateId && allTemplates.some(t => t.id === queryTemplateId)) {
      const element = document.getElementById(`template-card-${queryTemplateId}`);
      if (element) {
        // Add a slight delay to ensure rendering is complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [allTemplates, queryTemplateId]);

  const categories = useMemo(() => {
    const apiCategories = categoriesQuery.data?.categories || [];
    return ["all", "Unkategorisiert", ...apiCategories];
  }, [categoriesQuery.data]);

  const hasMoreTemplates = templatesQuery.data?.hasNextPage || false;
  
  // Prevent empty state flash by checking if we have data in query but not yet in state
  const isSyncing = (templatesQuery.data?.docs?.length ?? 0) > 0 && allTemplates.length === 0;
  const isInitialLoading = (templatesQuery.isLoading || isSyncing) && currentPage === 1;
  const isLoadingMore = templatesQuery.isLoading && currentPage > 1;

  const handleTemplateClick = (templateId: string) => {
    navigate(`/post-generator/create-post?templateId=${templateId}`);
  };

  const handleDeleteSuccess = async () => {
    await templatesQuery.refetch();
    setCurrentPage(1);
    setAllTemplates(prev => prev.filter(t => t.id !== templateToDelete?.id));
    setShowDeleteDialog(false);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length > 0) {
      bulkDeleteMutation.mutate(selectedIds, {
        onSuccess: async () => {
          toast.success(`${selectedIds.length} Templates erfolgreich gelöscht`);
          await templatesQuery.refetch();
          setCurrentPage(1);
          setAllTemplates(prev => prev.filter(t => !selectedIds.includes(t.id)));
          setSelectedIds([]);
          setShowDeleteDialog(false);
          setIsBulkDelete(false);
        },
        onError: (error) => {
          toast.error("Templates konnten nicht gelöscht werden", { error });
        }
      });
    }
  };

  const handleSelectionDelete = () => {
    if (selectedIds.length > 0) {
      setTemplateToDelete(null);
      setIsBulkDelete(true);
      setShowDeleteDialog(true);
    }
  };

  const handleEditSuccess = async () => {
    await templatesQuery.refetch();
    setCurrentPage(1);
  };

  const handleDuplicate = async (templateId: string) => {
    try {
      await duplicateMutation.mutateAsync(templateId);
      toast.success("Template dupliziert", {
        description: "Das Template wurde erfolgreich dupliziert.",
      });
      await templatesQuery.refetch();
      setCurrentPage(1);
    } catch (error) {
       toast.error("Fehler beim Duplizieren", {
        description: "Das Template konnte nicht dupliziert werden.",
        error,
      });
    }
  };

  const handleCreateSuccess = (templateId: string) => {
    navigate(`/post-generator/template-editor?templateId=${templateId}`);
  };

  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
  };

  const getAspectRatioDisplay = (aspectRatio: Template['aspectRatio']) => {
    const ratioMap = {
      '1:1': '1:1',
      '16:9': '16:9',
      '9:16': '9:16',
      '4:5': '4:5'
    };
    return ratioMap[aspectRatio] || aspectRatio;
  };

  const convertToGenericItem = (template: Template): GenericItem => {
    // Custom templates (originatesFrom == null) are restricted for users without MANAGE_CUSTOMER_TEMPLATE permission
    const isCustom = !template.meta?.importedFromTemplateId;
    const hasPermission = can(Permission.MANAGE_CUSTOMER_TEMPLATE);
    const isDisabled = isCustom && !hasPermission;

    return {
      id: template.id,
      title: template.title,
      previewContent: (
        <EntityPreviewFrame disabled={isDisabled} backgroundUrl={template.meta?.thumbnailPath}>
          {template.meta?.thumbnailPath ? (
            <img 
              src={template.meta.thumbnailPath} 
              alt={template.title} 
              className="max-w-full max-h-full rounded-sm object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-300">
              <LayoutTemplate className="h-12 w-12 mb-2" />
            </div>
          )}
        </EntityPreviewFrame>
      ),
      metadata: {
        badgeText: getAspectRatioDisplay(template.aspectRatio),
        categories: template.meta?.categories || [],
        isDisabled
      }
    };
  };

  const genericItems = useMemo(() => {
    return allTemplates.map(convertToGenericItem);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates, can]); // Add 'can' to dependencies

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      handleTemplateClick(id);
    },
    onEdit: (id: string) => {
      const template = allTemplates.find(t => t.id === id);
      if (template) {
        setTemplateToEdit(template);
        setShowEditDialog(true);
      }
    },
    customActions: [
      {
        label: 'Template Editor',
        icon: <PenTool className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => {
          navigate(`/post-generator/template-editor?templateId=${id}`);
        }
      },
      {
        label: 'Duplizieren',
        icon: <Copy className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => handleDuplicate(id),
        disabled: !can(Permission.MANAGE_CUSTOMER_TEMPLATE),
        badge: !can(Permission.MANAGE_CUSTOMER_TEMPLATE) ? (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-blue-200 text-blue-600 bg-blue-50">Pro</Badge>
        ) : undefined
      },
      {
        label: 'Info',
        icon: <Info className="h-3.5 w-3.5 text-black" />,
        onClick: (id: string) => {
          const template = allTemplates.find(t => t.id === id);
          if (template) {
            setSelectedTemplate(template);
            setShowInfoDialog(true);
          }
        }
      }
    ],
    onDelete: (id: string) => {
      const template = allTemplates.find(t => t.id === id);
      if (template) {
        setTemplateToDelete(template);
        setIsBulkDelete(false);
        setShowDeleteDialog(true);
      }
    }
  };

  const handleLoadMore = () => {
    if (hasMoreTemplates && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <SubPageLayout 
      pageTitle="Deine Templates" 
      icon={<LayoutTemplate />} 
      navigateBackCallback={() => navigate(-1)}
      dataOnboarding={isOnboardingImportFlow ? "customer-templates-page" : undefined}
    >
      <DataView 
        viewMode={viewMode} 
        onViewModeChange={handleViewModeChange}
        isLoading={isInitialLoading}
        isEmpty={allTemplates.length === 0}
        selectedIds={selectedIds}
        onMultiSelect={setSelectedIds}
        onSelectionDelete={handleSelectionDelete}
      >
        <div data-onboarding="customer-templates-toolbar" className="bg-white flex-shrink-0 border-b border-gray-100">
        {state.isInfoBannerVisible && (
          <div className="p-4 pb-0">
            <PageInfoBanner
              title="Template auswählen und loslegen!"
              description="Wählen Sie ein Template aus der Galerie, um sofort mit der Erstellung professioneller Social Media Posts zu beginnen."
              icon={LayoutTemplate}
              variant="blue"
              onClose={() => setInfoBannerVisible(false)}
              ctaLabel="Mehr erfahren"
              onCtaClick={() => setShowInfoDialog(true)}
            />
          </div>
        )}
        
        <DataViewToolbar
          searchQuery={searchTerm}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Templates suchen..."
          filterParams={categories.map(category => ({
            key: category,
            label: category === "all" ? "Alle Kategorien" : category,
            value: category
          }))}
          selectedFilter={selectedCategory}
          onFilterChange={(val) => setSettings({ selectedCategory: val })}
          filterLabel="Kategorie"
          infoText={`${templatesQuery.data?.count || 0} Templates`}
          leading={
            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_CUSTOMER_TEMPLATE} requiredPlan={PlanKey.PRO}>
              <PrimaryActionButton label="Neues Template" icon={Plus} onClick={() => setShowCreateDialog(true)} />
            </PermissionFeatureWrapper>
          }
        />
      </div>

      <DataViewLayout>
        <DataViewContent
           items={genericItems}
           renderGridItem={(item) => {
             const isDisabled = item.metadata?.isDisabled;
             const isHighlightedTemplate = isHighlightActive && item.id === activeHighlightedTemplateId;
             const itemActions = isDisabled ? {
               ...genericActions,
               onItemClick: () => setShowUpgradeDialog(true), // Intercept click
               onEdit: undefined, // Remove edit
               customActions: genericActions.customActions?.filter(
                 a => a.label !== 'Template Editor' && a.label !== 'Duplizieren'
               ).map(a => ({ ...a, disabled: true })), // Disable other custom actions visually
               // Keep onDelete
             } : genericActions;

             return (
               <div
                 id={`template-card-${item.id}`}
                 data-onboarding={isHighlightedTemplate ? "customer-template-highlight" : undefined}
                 className="h-full"
               >
                 <DataViewGridCard 
                   item={item} 
                   actions={itemActions} 
                   className={isDisabled ? "opacity-75" : isHighlightedTemplate ? "border-blue-500 ring-2 ring-blue-500/40 shadow-lg shadow-blue-500/10" : ""} 
                 />
               </div>
             );
           }}
           renderListItem={(item) => {
             const isDisabled = item.metadata?.isDisabled;
             const isHighlightedTemplate = isHighlightActive && item.id === activeHighlightedTemplateId;
             const itemActions = isDisabled ? {
               ...genericActions,
               onItemClick: () => setShowUpgradeDialog(true),
               onEdit: undefined,
               customActions: genericActions.customActions?.filter(
                 a => a.label !== 'Template Editor' && a.label !== 'Duplizieren'
               ).map(a => ({ ...a, disabled: true })),
               // Keep onDelete
             } : genericActions;

             return (
               <div id={`template-card-${item.id}`} data-onboarding={isHighlightedTemplate ? "customer-template-highlight" : undefined}>
                 <DataViewListCard 
                   item={item} 
                   actions={itemActions} 
                   className={isDisabled ? "opacity-75" : isHighlightedTemplate ? "border-blue-500 ring-2 ring-blue-500/30 rounded-xl" : ""}
                 />
               </div>
             )
           }}
           loadingIndicator={<DataViewLoadingState />}
           emptyState={
             <DataViewEmptyState
               icon={<LayoutTemplate className="h-8 w-8 text-gray-400" />}
               title={searchTerm.trim() !== "" || selectedCategory !== "all" ? 'Keine Templates gefunden' : 'Noch keine Templates vorhanden'}
               description={searchTerm.trim() !== "" || selectedCategory !== "all" 
                 ? 'Versuchen Sie andere Suchbegriffe oder Filter.'
                 : 'Importieren Sie professionelle Templates aus dem Template Store, um direkt mit der Erstellung Ihrer Posts zu beginnen.'
               }
               action={
                !(searchTerm.trim() !== "" || selectedCategory !== "all") ? (
                  <Button 
                    className="h-9 px-4 bg-gray-900 text-white hover:bg-gray-800 rounded-md"
                    onClick={() => navigate('/post-generator/store')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Aus Template Store importieren
                  </Button>
                ) : undefined
              }
             />
           }
        />

        <DataViewLoadMore
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={hasMoreTemplates}
        />
      </DataViewLayout>
        
      <TemplateInfoDialog
        open={showInfoDialog}
        onOpenChange={setShowInfoDialog}
      />

      {templateToEdit && (
        <EditTemplateDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          template={templateToEdit}
          onSuccess={handleEditSuccess}
        />
      )}

      {templateToDelete && !isBulkDelete && (
        <DeleteTemplateDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          templateId={templateToDelete.id}
          templateTitle={templateToDelete.title}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {isBulkDelete && (
        <StandardDeleteDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title={`${selectedIds.length} Templates löschen`}
            description="Möchten Sie diese Templates wirklich unwiderruflich löschen?"
            itemsList={selectedIds.map(id => {
              const t = allTemplates.find(t => t.id === id);
              return {
                title: t ? t.title : id
              };
            })}
            onDelete={handleBulkDeleteConfirm}
            isLoading={bulkDeleteMutation.isPending}
        />
      )}
      
      <CreateTemplateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleCreateSuccess}
      />
      <UpgradePlanDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        requiredPlan={PlanKey.PRO}
      />
      </DataView>
    </SubPageLayout>
  );
};

export default Templates;
